import { FC, useCallback, useEffect, useRef } from "react";
import { DrawingFinishEvent } from "@evergis/sgis/es/controls/Control";
import { Rectangle } from "@evergis/sgis/es/controls/Rectangle";
import { useMapContext } from "@evergis/react";
import polygonClipping, { Geom } from "polygon-clipping";

import { BaseRectCreator } from "../../../../../react-sgis/controls/baseControls/BaseRectCreator";
import { ShadowedPolySymbol } from "../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";

import { useGeometrySelectionContext } from "../../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useGeometrySelection } from "../../../../../hooks/geometrySelection/useGeometrySelection";
import { useSelectionEdit } from "./hooks/useSelectionEdit";
import { createPolygonFeature } from "./utils/createPolygonFeature";
import { getRingsWithoutDuplicates } from "../../../../../utils/geometry";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./hooks/useSelectionSymbols";

import { GeometrySelectionToolTypes, GeometrySelectionTypes } from "../types";
import { SelectionProps } from "./types";

export const RectSelection: FC<SelectionProps> = ({ mode }) => {
  const { addFeature, replaceFeature } = useGeometrySelectionContext();
  const [{ history }] = useGeometrySelection();
  const { map, painter } = useMapContext();
  const createControl = useRef<BaseRectCreator>();
  const symbols = useSelectionSymbols();

  const switchMode = useCallback((create: boolean) => {
    if (createControl.current) {
      createControl.current.isActive = create;
    }
  }, []);

  const createFeature = useCallback(
    (event: DrawingFinishEvent<Rectangle & { _rings?: [number, number][][] }>) => {
      const shouldRewrite =
        history.features.length > 0 &&
        history.step > -1 &&
        [GeometrySelectionToolTypes.Unite, GeometrySelectionToolTypes.Crop].includes(mode);

      let result;

      if (mode === GeometrySelectionToolTypes.Unite && history.features.length > 0 && history.step > -1) {
        result = polygonClipping
          .union([...history.features[history.step].map(item => item._rings), event.feature._rings] as Geom)
          // polygonClipping.union adds last point duplicate to polygons without intersection
          .map(getRingsWithoutDuplicates)
          .map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            ),
          );
      } else if (mode === GeometrySelectionToolTypes.Crop && history.features.length > 0 && history.step > -1) {
        result = polygonClipping
          .difference(
            [...history.features[history.step].map(item => item._rings)] as Geom,
            event.feature._rings as Geom,
          )
          .map(getRingsWithoutDuplicates)
          .map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            ),
          );
      } else {
        result = createPolygonFeature(
          [...event.feature._rings],
          map.crs,
          symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
        );
      }

      if (mode === GeometrySelectionToolTypes.Replace && history.step > -1) {
        replaceFeature(result);
      } else {
        addFeature(result, shouldRewrite);
      }
    },
    [history.features, history.step, mode, map.crs, symbols, replaceFeature, addFeature],
  );

  const offEvents = useCallback(() => {
    createControl.current.off("drawingFinish", createFeature);
  }, [createFeature]);

  const clearProperties = useCallback(() => {
    createControl.current = undefined;
  }, []);

  const initCreateMode = useCallback(() => {
    if (!createControl.current) {
      createControl.current = new BaseRectCreator(map, painter, {
        isActive: false,
        useTempLayer: true,
        symbol: symbols[GeometrySelectionSymbolType.polygonCreate].clone() as ShadowedPolySymbol,
        createByClick: true,
        showAreaLabel: true,
      });

      createControl.current.on("drawingFinish", createFeature);
    }
  }, [createFeature, map, painter]);

  useEffect(() => {
    initCreateMode();
    switchMode(mode !== GeometrySelectionToolTypes.Edit);

    return () => {
      switchMode(false);
      offEvents();
      clearProperties();
    };
  }, [initCreateMode]); // eslint-disable-line

  useSelectionEdit(GeometrySelectionTypes.Polygon, map, painter, mode, switchMode);

  return null;
};
